import { useEffect, useState, type FormEvent } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Button,
  Card,
  PasswordStrengthMeter,
  describeAuthFormError,
  formErrorClassName,
  formFieldClassName,
  formInputClassName,
  linkClassName,
  secondaryTextClassName,
  INVITATION_CONTINUATION_STATE,
  useAuth,
  validationErrorClassName,
  type AuthFormError,
} from '@monecolier/ui';
import { getPrivacyPolicy } from '@monecolier/api-client';
import { invitationContinuation } from '../lib/invitationContinuation';

const REDIRECT_AFTER_REGISTER = '/';

// PAR-01 : URL du portail école (web-admin), pour le lien croisé "Je représente une
// école" ci-dessous — voir docs/history/PAR.md, section "PAR-01", et le commentaire équivalent
// dans apps/web-admin/src/pages/Register.tsx pour le détail complet du schéma de
// cette variable.
const ADMIN_APP_URL = import.meta.env.VITE_ADMIN_APP_URL ?? '';

// Miroir de apps/api/src/lib/password.ts#MIN_PASSWORD_LENGTH — les deux apps
// front-end n'ont pas d'accès direct au code d'apps/api (aucun package partagé entre
// eux), donc cette constante est dupliquée volontairement, comme d'autres valeurs
// synchronisées à la main dans ce monorepo (voir packages/types, énumérations
// alignées sur schema.prisma). Ce n'est qu'une pré-validation UX : le backend reste
// la seule source de vérité qui rejette réellement un mot de passe trop court.
const MIN_PASSWORD_LENGTH = 8;

export function Register() {
  const { register, login, apiClient } = useAuth();
  const navigate = useNavigate();
  // AUT-33 : présent UNIQUEMENT quand on arrive depuis un lien d'invitation
  // (AcceptParentInvitation/AcceptGuardianInvitation) — absent, ce formulaire
  // reste strictement inchangé.
  const [searchParams] = useSearchParams();
  const continuation = invitationContinuation.read(searchParams);
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  // AUT-17 : deux champs SÉPARÉS (plus de "Nom complet" unique) — le
  // serveur en dérive `fullName`, jamais l'inverse.
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [phone, setPhone] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [validationError, setValidationError] = useState<string | null>(null);
  const [error, setError] = useState<AuthFormError | null>(null);
  // CMP-02 : JAMAIS pré-cochée — voir le commentaire équivalent dans
  // apps/web-admin/src/pages/Register.tsx pour le détail complet.
  const [consentAccepted, setConsentAccepted] = useState(false);
  const [policyText, setPolicyText] = useState<string | null>(null);
  const [policyExpanded, setPolicyExpanded] = useState(false);

  useEffect(() => {
    getPrivacyPolicy(apiClient)
      .then((policy) => setPolicyText(policy.text))
      .catch(() => {
        // Best-effort — voir le commentaire équivalent dans web-admin.
      });
    // Chargement UNIQUE au montage — `apiClient` référence stable
    // (packages/ui/src/auth/AuthContext.tsx), aucune autre dépendance.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError(null);
    setValidationError(null);

    // Validation côté client AVANT tout appel réseau — miroir de la validation
    // backend (apps/api/src/routes/auth.ts), mais appliquée ici sans attendre un
    // aller-retour HTTP pour un cas qu'on peut détecter localement.
    if (password.length < MIN_PASSWORD_LENGTH) {
      setValidationError(t('register.passwordTooShort', { min: MIN_PASSWORD_LENGTH }));
      return;
    }

    if (!consentAccepted) {
      setValidationError(t('register.consentRequired'));
      return;
    }

    setSubmitting(true);
    try {
      // PAR-03 : `phone` transmis DIRECTEMENT à register() — POST /auth/register crée
      // le `User` ET le profil `Parent` dans la MÊME transaction côté serveur (voir
      // apps/api/src/routes/auth.ts) quand un téléphone est fourni. Corrige un bug
      // réel de PAR-01 (PR #49) : un appel register()+login() séparé suivi d'un
      // troisième appel createParentProfile() pouvait laisser un `User` orphelin
      // (email déjà consommé, aucun profil parent) si CE dernier appel échouait —
      // voir docs/architecture.md, section "PAR-03", pour le diagnostic complet. Le
      // message d'erreur backend (téléphone invalide/manquant) est affiché tel quel,
      // comme le reste de ce formulaire — voir describeAuthFormError plus bas.
      await register({ email, password, firstName, lastName, phone, consentAccepted });
      // Inscription -> connexion immédiate avec les mêmes identifiants : le backend
      // active le compte tout de suite (décision AUT-01, pas de vérification email en
      // V1) — pas de raison de faire retaper l'email/mot de passe qu'on vient de saisir.
      await login({ email, password });
      // AUT-33 : inscription depuis un lien d'invitation — retour direct sur la
      // page d'acceptation, qui rattache l'enfant puis redirige vers l'accueil.
      if (continuation) {
        navigate(invitationContinuation.acceptPath(continuation), { replace: true, state: INVITATION_CONTINUATION_STATE });
        return;
      }
      navigate(REDIRECT_AFTER_REGISTER);
    } catch (err) {
      // describeAuthFormError() journalise TOUJOURS l'erreur réelle via
      // console.error avant de la traduire — voir packages/ui/src/auth/
      // authFormError.ts pour le bug réel (CORS silencieux) que cette
      // journalisation permanente permet désormais de diagnostiquer. Le message
      // backend d'un 409 ("Un compte existe déjà avec cet email.") est déjà
      // clair — repris tel quel, jamais reformulé.
      setError(describeAuthFormError(err));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Card title={t('register.title')}>
      {/* PAR-01 : voir le commentaire équivalent dans apps/web-admin/src/pages/
          Register.tsx — même différenciation, sens inverse. */}
      {/* UX-13 : `flex-wrap` défensif — même risque de débordement que le bandeau
          d'actions de Home.tsx à ~320px, deux libellés assez longs
          ("Je représente une école") côte à côte sans marge. */}
      <div className="flex flex-wrap gap-2 mb-2" role="group" aria-label={t('register.accountTypeGroupLabel')}>
        <span
          aria-current="true"
          data-testid="register-portal-parent"
          className="rounded-full bg-brand-100 px-3 py-1 text-sm font-medium text-brand-900"
        >
          {t('register.iAmAParent')}
        </span>
        <a
          href={ADMIN_APP_URL ? `${ADMIN_APP_URL}/register` : '#'}
          data-testid="register-portal-school-link"
          className={linkClassName}
        >
          {t('register.iRepresentASchool')}
        </a>
      </div>
      <form onSubmit={handleSubmit} className="flex flex-col gap-4">
        <label className={formFieldClassName}>
          {t('register.firstName')}
          <input
            value={firstName}
            onChange={(event) => setFirstName(event.target.value)}
            required
            autoComplete="given-name"
            data-testid="register-firstname-input"
            className={formInputClassName}
          />
        </label>
        <label className={formFieldClassName}>
          {t('register.lastName')}
          <input
            value={lastName}
            onChange={(event) => setLastName(event.target.value)}
            required
            autoComplete="family-name"
            data-testid="register-lastname-input"
            className={formInputClassName}
          />
        </label>
        <label className={formFieldClassName}>
          {t('register.email')}
          <input
            type="email"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            required
            autoComplete="email"
            className={formInputClassName}
          />
        </label>
        <label className={formFieldClassName}>
          {t('register.phone')}
          <input
            type="tel"
            value={phone}
            onChange={(event) => setPhone(event.target.value)}
            required
            autoComplete="tel"
            placeholder="+221 77 123 45 67"
            className={formInputClassName}
          />
        </label>
        <label className={formFieldClassName}>
          {t('register.password')}
          <input
            type="password"
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            required
            autoComplete="new-password"
            className={formInputClassName}
          />
        </label>
        <PasswordStrengthMeter password={password} />
        {/* CMP-02 : voir le commentaire équivalent dans apps/web-admin/src/pages/
            Register.tsx pour le détail complet. */}
        <div className="flex flex-col gap-2">
          <label className="flex items-start gap-2 text-sm text-gray-700">
            <input
              type="checkbox"
              checked={consentAccepted}
              onChange={(event) => setConsentAccepted(event.target.checked)}
              data-testid="register-consent-checkbox"
            />
            <span>
              {t('register.consentPrefix')}{' '}
              <button
                type="button"
                onClick={() => setPolicyExpanded((expanded) => !expanded)}
                className={linkClassName}
                data-testid="register-consent-toggle"
              >
                {t('register.consentPolicyLink')}
              </button>
              .
            </span>
          </label>
          {policyExpanded ? (
            <div
              className="max-h-40 overflow-y-auto rounded-md border border-gray-200 bg-gray-50 p-3 text-xs text-gray-600"
              data-testid="register-consent-policy-text"
            >
              {policyText ?? t('register.policyLoading')}
            </div>
          ) : null}
        </div>
        {validationError ? (
          <p role="alert" data-testid="register-validation-error" className={validationErrorClassName}>
            {validationError}
          </p>
        ) : null}
        {error ? (
          <p role="alert" data-testid="register-error" className={formErrorClassName(error.kind)}>
            {error.message}
          </p>
        ) : null}
        <Button type="submit" disabled={submitting || !consentAccepted}>
          {submitting ? t('register.submitting') : t('register.submit')}
        </Button>
      </form>
      <p className={secondaryTextClassName}>
        {t('register.alreadyAccount')} <Link to={invitationContinuation.authPath('/login', continuation)} className={linkClassName}>{t('register.login')}</Link>
      </p>
    </Card>
  );
}
